// src/services/notificationService.js
import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../firebase/config';
import axios from 'axios';
import config from '../config';

class NotificationService {
  constructor() {
    this.vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || 'your-vapid-key';
    this.fcmToken = null;
  }

  // Initialize notification service
  async initialize() {
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.log('This browser does not support notifications');
        return false;
      }

      // Register service worker first
      await this.registerServiceWorker();

      // Request notification permission automatically
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Get FCM token (will only send to server if user is authenticated)
      await this.getFCMToken();

      // Listen for foreground messages
      this.onForegroundMessage();

      return true;
    } catch (error) {
      console.error('Error initializing notification service:', error);
      return false;
    }
  }

  // Initialize notifications after login
  async initializeAfterLogin() {
    try {
      console.log('Initializing notifications after login...');
      
      // Check if user is authenticated
      if (!config.token || !config.user) {
        console.log('User not authenticated, cannot initialize notifications');
        return false;
      }

      // First try to sync existing token
      const tokenSynced = await this.syncTokenAfterLogin();
      
      if (!tokenSynced) {
        // No existing token, initialize normally
        const initialized = await this.initialize();
        
        if (initialized) {
          console.log('Notifications initialized successfully after login');
          return true;
        } else {
          console.log('Failed to initialize notifications after login');
          return false;
        }
      } else {
        // Token was synced, just set up message listening
        await this.registerServiceWorker();
        this.onForegroundMessage();
        console.log('Existing FCM token synced successfully after login');
        return true;
      }
    } catch (error) {
      console.error('Error initializing notifications after login:', error);
      return false;
    }
  }

  // Clean up notifications on logout
  async cleanupOnLogout() {
    try {
      console.log('Cleaning up notifications on logout...');
      
      // Remove FCM token from local storage
      localStorage.removeItem('fcm_token');
      
      // Clear the current token
      this.fcmToken = null;
      
      // Optionally, you could also send a request to remove the token from server
      // but since the user is logging out, the server should handle this based on session
      
      console.log('Notification cleanup completed');
      return true;
    } catch (error) {
      console.error('Error cleaning up notifications on logout:', error);
      return false;
    }
  }

  // Request notification permission
  async requestPermission() {
    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Register service worker
  async registerServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('Service Worker registered successfully:', registration);
        return registration;
      } catch (error) {
        console.error('Service Worker registration failed:', error);
        throw error;
      }
    }
  }

  // Get FCM token
  async getFCMToken() {
    try {
      const token = await getToken(messaging, {
        vapidKey: this.vapidKey
      });
      
      if (token) {
        console.log('FCM Token:', token);
        this.fcmToken = token;
        
        // Store token locally
        localStorage.setItem('fcm_token', token);
        
        // Only send token to server if user is authenticated
        if (config.token && config.user) {
          console.log('User is authenticated, updating FCM token on server...');
          await this.updateFCMToken(token);
        } else {
          console.log('User not authenticated, FCM token stored locally only');
        }
        
        return token;
      } else {
        console.log('No registration token available.');
        return null;
      }
    } catch (error) {
      console.error('An error occurred while retrieving token:', error);
      throw error;
    }
  }

  // Update FCM token on server
  async updateFCMToken(fcmToken) {
    try {
      // Check if user is authenticated before making API call
      if (!config.token || !config.user) {
        console.log('User not authenticated, skipping FCM token update API call');
        return { success: false, message: 'User not authenticated' };
      }

      console.log('Sending FCM token to server for authenticated user...');
      
      const response = await axios.post(
        `${config.apiUrl}/update-fcm-token`,
        {
          fcm_token: fcmToken,
          device_type: 'web'
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': config.token
          }
        }
      );

      console.log('FCM token updated successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating FCM token:', error);
      throw error;
    }
  }

  // Listen for foreground messages
  onForegroundMessage() {
    onMessage(messaging, (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Show notification in foreground
      this.showNotification(payload);
    });
  }

  // Show notification
  showNotification(payload) {
    const { title, body, icon } = payload.notification || {};
    
    if (Notification.permission === 'granted') {
      const notification = new Notification(title || 'Biznie App', {
        body: body || 'You have a new notification',
        icon: icon || '/favicon.png',
        badge: '/favicon.png',
        tag: 'biznie-notification',
        data: payload.data
      });

      // Handle notification click
      notification.onclick = function(event) {
        event.preventDefault();
        window.focus();
        notification.close();
        
        // Handle navigation based on notification data
        if (payload.data && payload.data.url) {
          window.location.href = payload.data.url;
        }
      };

      // Auto close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  // Get current FCM token
  getCurrentToken() {
    return this.fcmToken || localStorage.getItem('fcm_token');
  }

  // Check if notifications are enabled
  isNotificationEnabled() {
    return Notification.permission === 'granted';
  }

  // Refresh FCM token
  async refreshToken() {
    try {
      await this.getFCMToken();
    } catch (error) {
      console.error('Error refreshing FCM token:', error);
    }
  }

  // Update existing token to server after login
  async syncTokenAfterLogin() {
    try {
      const existingToken = localStorage.getItem('fcm_token');
      
      if (existingToken && config.token && config.user) {
        console.log('Syncing existing FCM token with server after login...');
        await this.updateFCMToken(existingToken);
        this.fcmToken = existingToken;
        return true;
      } else if (!existingToken) {
        console.log('No existing FCM token found, will generate new one');
        return false;
      } else {
        console.log('User not authenticated, cannot sync token');
        return false;
      }
    } catch (error) {
      console.error('Error syncing token after login:', error);
      return false;
    }
  }
}

// Create and export singleton instance
const notificationServiceInstance = new NotificationService();

export { notificationServiceInstance };
export default notificationServiceInstance;
